/**
 * useSubscribe — SOLE network owner for the two franqueado Wave-2 routes.
 *
 *   GET  subscriptions/available-plans  → lists plans available for self-subscribe
 *   POST subscriptions/subscribe        → initiates a subscription (PENDING only)
 *
 * All fetch is here (no_httpservice_in_vue rule forbids fetch in .vue files).
 * Module-scope singleton state; call `_reset()` in afterEach for test isolation.
 *
 * PCI note: no card/cartao/cvv/pan/token field may ever be added here.
 */
import { ref } from 'vue'
import { httpService, type HttpError } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { Subscriptions_PlansResponseDados } from '../contracts/Subscriptions_PlansResponse'
import type { Subscriptions_PlanRow } from '../contracts/Subscriptions_PlanRow'
import type { Subscriptions_SubscribeInput } from '../contracts/Subscriptions_SubscribeInput'
import type { Subscriptions_SubscribeResponseDados } from '../contracts/Subscriptions_SubscribeResponse'

// Side-effect imports: register contracts with the responseContract registry.
import '../contracts/Subscriptions_PlansResponse'
import '../contracts/Subscriptions_PlanRow'
import '../contracts/Subscriptions_SubscribeInput'
import '../contracts/Subscriptions_SubscribeResponse'
import '../contracts/Subscriptions_AvailablePlansInput'

// ── Module-scope singleton state ─────────────────────────────────────────────

const plans = ref<Subscriptions_PlanRow[]>([])
/**
 * emptyReason (D6) — mirrors dados.empty_reason from the last available-plans
 * response. 'pais_sem_mercado' means the franchise's country has no billing
 * market configured (named reason — the catalog is intentionally empty).
 * Null when plans exist or no named reason was given.
 */
const emptyReason = ref<string | null>(null)

// ── Internal helpers ─────────────────────────────────────────────────────────

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

// ── Public composable ────────────────────────────────────────────────────────

export function useSubscribe() {
  /**
   * listAvailablePlans — GET subscriptions/available-plans.
   *
   * Uses the franqueado-facing endpoint (NOT subscriptions/plans — that is the
   * staff route). Stores dados.rows into the module-scope `plans` ref.
   *
   * @param franquiaId — when provided, filters to that franchise's market
   *   currency. D6: when the franchise's country has no billing market the
   *   backend returns rows=[] with empty_reason='pais_sem_mercado' (no BR/BRL
   *   fallback on this seam anymore).
   */
  async function listAvailablePlans(franquiaId?: number): Promise<void> {
    const params = franquiaId != null ? `?franquia_id=${franquiaId}` : ''
    try {
      const raw = await httpService.get(
        sistemaUrl(`subscriptions/available-plans${params}`),
        { responseContract: 'Subscriptions_PlansResponse', skipErrorDisplay: false },
      )
      const resp = raw as { dados: Subscriptions_PlansResponseDados }
      plans.value = resp.dados.rows
      emptyReason.value = resp.dados.empty_reason ?? null
      log('useSubscribe', 'listAvailablePlans', 'Planos disponíveis carregados', { count: plans.value.length, franquiaId, emptyReason: emptyReason.value })
    } catch (err) {
      log('useSubscribe', 'listAvailablePlans', 'Erro ao carregar planos disponíveis', {
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      throw err
    }
  }

  /**
   * subscribe — POST subscriptions/subscribe.
   *
   * Binds EXACTLY {franquia_id, plan_id} — no other fields.
   * Returns dados ({checkout_url, subscription_id, status, pending, plan_id}).
   * status is always 'pending' on creation — no optimistic activation here.
   */
  async function subscribe(input: Subscriptions_SubscribeInput): Promise<Subscriptions_SubscribeResponseDados> {
    const body: Subscriptions_SubscribeInput = {
      franquia_id: input.franquia_id,
      plan_id: input.plan_id,
      provider_code: input.provider_code,
    }
    try {
      const raw = await httpService.post(
        sistemaUrl('subscriptions/subscribe'),
        body,
        {
          inputContract: 'Subscriptions_SubscribeInput',
          responseContract: 'Subscriptions_SubscribeResponse',
          skipErrorDisplay: false,
          // A recusa ('plan_not_found', 'live_subscription_exists', país sem
          // mercado…) chega em HTTP 200 com o envelope em success:false. Sem
          // esta opção, `resp.dados.checkout_url` estoura um TypeError sem
          // nome — ou pior, um redirect para `undefined`.
          throwOnEnvelopeFailure: 'subscribe_failed',
        },
      )
      const resp = raw as { dados: Subscriptions_SubscribeResponseDados }
      log('useSubscribe', 'subscribe', 'Assinatura iniciada (pending)', {
        franquia_id: input.franquia_id,
        plan_id: input.plan_id,
        checkout_url: resp.dados.checkout_url,
        subscription_id: resp.dados.subscription_id,
        status: resp.dados.status,
      })
      return resp.dados
    } catch (err) {
      log('useSubscribe', 'subscribe', 'Erro ao iniciar assinatura', {
        franquia_id: input.franquia_id,
        plan_id: input.plan_id,
        message: (err as Error)?.message,
        status: (err as HttpError)?.status,
      })
      throw err
    }
  }

  /**
   * redirectToCheckout — thin, spy-able seam for the checkout redirect.
   *
   * Calls // New tab, always: the SPA stays alive and the user never needs the
    // browser back button to return from the gateway (2026-08-20).
    window.open(url, '_blank', 'noopener') so the .vue layer stays free of window.*
   * and tests can spy on this method instead of patching window.location.
   */
  function redirectToCheckout(url: string): void {
    log('useSubscribe', 'redirectToCheckout', 'Redirecionando para checkout', { url })
    // New tab, always: the SPA stays alive and the user never needs the
    // browser back button to return from the gateway (2026-08-20).
    window.open(url, '_blank', 'noopener')
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    plans.value = []
    emptyReason.value = null
  }

  return {
    // reactive state
    plans,
    emptyReason,
    // methods
    listAvailablePlans,
    subscribe,
    redirectToCheckout,
    _reset,
  }
}
